import {
  IPayment,
  IPropertyLabelFields,
} from "../types/ownerDashboard.types";

export interface IPropertyLabelSource extends IPropertyLabelFields {
  kenlo_property_id: string;
}

const GENERIC_PROPERTY_LABEL_PATTERN = /^Imóvel(?:\s+Kenlo)?\s+\d[\d/]*$/i;

export function isUsablePropertyLabel(label: string | null | undefined): boolean {
  const normalized = label?.trim();

  if (!normalized) {
    return false;
  }

  if (isGenericPropertyLabel(normalized)) {
    return false;
  }

  return !/^Imóvel #\d+$/.test(normalized);
}

export function isGenericPropertyLabel(label: string | null | undefined): boolean {
  const normalized = label?.trim();

  if (!normalized) {
    return false;
  }

  return GENERIC_PROPERTY_LABEL_PATTERN.test(normalized);
}

export function normalizeKenloPropertyId(
  kenloPropertyId: string | number | null | undefined
): string {
  const raw = String(kenloPropertyId ?? "").trim();

  if (!raw) {
    return "";
  }

  const slashIndex = raw.indexOf("/");

  if (slashIndex >= 0) {
    return raw.slice(0, slashIndex);
  }

  return raw;
}

export function formatPropertyLabel(payment: IPayment): string {
  const apiLabel = payment.property_label?.trim();
  const address = payment.address?.trim();
  const complement = payment.complement?.trim();
  const propertyId = normalizeKenloPropertyId(payment.kenlo_property_id);

  if (apiLabel && !isGenericPropertyLabel(apiLabel)) {
    return apiLabel;
  }

  if (!address) {
    return `Imóvel ${propertyId}`;
  }

  if (!complement) {
    return address;
  }

  return `${address} - ${complement}`;
}

export function buildPropertyLabelMap(payments: IPayment[]): Map<string, string> {
  const labels = new Map<string, string>();
  const paymentsByKenloId = new Map<string, IPayment>();

  payments.forEach((payment) => {
    const propertyId = normalizeKenloPropertyId(payment.kenlo_property_id);

    if (!propertyId) {
      return;
    }

    const currentPayment = paymentsByKenloId.get(propertyId);
    const hasLabelSource = Boolean(
      payment.property_label?.trim() || payment.address?.trim()
    );

    if (!currentPayment || (hasLabelSource && !currentPayment.property_label?.trim() && !currentPayment.address?.trim())) {
      paymentsByKenloId.set(propertyId, payment);
    }
  });

  paymentsByKenloId.forEach((payment, propertyId) => {
    const label = formatPropertyLabel(payment);

    if (isGenericPropertyLabel(label)) {
      return;
    }

    labels.set(propertyId, label);
  });

  return labels;
}

export function resolveApiPropertyLabel(source: IPropertyLabelSource): string | null {
  const apiLabel = source.property_label?.trim();
  const apiAddress = source.property_address?.trim();

  if (apiLabel && !isGenericPropertyLabel(apiLabel)) {
    return apiLabel;
  }

  if (apiAddress) {
    return apiAddress;
  }

  return null;
}

export function resolvePropertyDisplayLabel(
  source: IPropertyLabelSource,
  labelMap?: Map<string, string>
): string {
  const normalizedId = normalizeKenloPropertyId(source.kenlo_property_id);

  if (labelMap && normalizedId) {
    const mappedLabel = labelMap.get(normalizedId);

    if (mappedLabel) {
      return mappedLabel;
    }
  }

  const apiLabel = resolveApiPropertyLabel(source);

  if (apiLabel) {
    return apiLabel;
  }

  return normalizedId ? `Imóvel ${normalizedId}` : "Imóvel";
}

export function resolveKenloPropertyFilterLabel(
  kenloPropertyId: string,
  labelMap: Map<string, string>,
  apiSource?: IPropertyLabelSource
): string {
  const normalizedId = normalizeKenloPropertyId(kenloPropertyId);
  const paymentLabel = labelMap.get(normalizedId);

  if (paymentLabel && !isGenericPropertyLabel(paymentLabel)) {
    return paymentLabel;
  }

  if (apiSource) {
    const apiLabel = resolveApiPropertyLabel(apiSource);

    if (apiLabel) {
      return apiLabel;
    }
  }

  return normalizedId ? `Imóvel ${normalizedId}` : "Imóvel";
}

export function resolveOwnerPropertyCardLabel(
  source: {
    id: number;
    kenlo_property_id: string;
    property_label?: string | null;
  },
  propertyFilterOptions: { kenloPropertyId: string; label: string }[],
  labelMap: Map<string, string>
): string {
  const apiLabel = source.property_label?.trim();

  if (isUsablePropertyLabel(apiLabel)) {
    return apiLabel!;
  }

  const kenloPropertyId = normalizeKenloPropertyId(source.kenlo_property_id);
  const filterLabel = propertyFilterOptions.find(
    (option) => option.kenloPropertyId === kenloPropertyId
  )?.label;

  if (isUsablePropertyLabel(filterLabel)) {
    return filterLabel!;
  }

  const paymentLabel = labelMap.get(kenloPropertyId);

  if (isUsablePropertyLabel(paymentLabel)) {
    return paymentLabel!;
  }

  return `Imóvel ${kenloPropertyId}`;
}

export function buildEnrichedPropertyLabelMap(
  payments: IPayment[],
  apiSources: IPropertyLabelSource[] = []
): Map<string, string> {
  const labelMap = buildPropertyLabelMap(payments);

  apiSources.forEach((source) => {
    const normalizedId = normalizeKenloPropertyId(source.kenlo_property_id);

    if (!normalizedId || labelMap.has(normalizedId)) {
      return;
    }

    const apiLabel = resolveApiPropertyLabel(source);

    if (apiLabel) {
      labelMap.set(normalizedId, apiLabel);
    }
  });

  return labelMap;
}

export function getPropertyLabel(
  source: string | IPropertyLabelSource,
  labelMap: Map<string, string>
): string {
  if (typeof source === "string") {
    const normalizedId = normalizeKenloPropertyId(source);
    const mappedLabel = labelMap.get(normalizedId);

    if (mappedLabel && !isGenericPropertyLabel(mappedLabel)) {
      return mappedLabel;
    }

    return `Imóvel ${normalizedId}`;
  }

  return resolvePropertyDisplayLabel(source, labelMap);
}

export function disambiguatePropertyLabels(
  options: { kenloPropertyId: string; label: string }[]
): { kenloPropertyId: string; label: string }[] {
  const labelCounts = options.reduce<Record<string, number>>((counts, option) => {
    counts[option.label] = (counts[option.label] ?? 0) + 1;
    return counts;
  }, {});

  return options.map((option) => {
    if (labelCounts[option.label] <= 1) {
      return option;
    }

    return {
      ...option,
      label: `${option.label} (Contrato ${option.kenloPropertyId})`,
    };
  });
}
